import { type Leaf, Mark, type Text } from '@gitbook/slate';
import { Set as ISet, List } from '@gitbook/util-immutable';
import { diffOrderedLists } from '@gitbook/util-tree-merge';
import { assertNever } from '@gitbook/util-ts';

import { MARK_DIFF_ADDED, MARK_DIFF_DELETED } from '../types';
import { splitWords } from './splitWords';

/**
 * Diff the content of a text node; and returns a highlighted text node.
 */
export function diffTexts(origin: Text, modified: Text): Text {
    const diffs = diffOrderedLists(splitTextInWords(origin), splitTextInWords(modified), (a, b) =>
        a.equals(b)
    );

    return origin.setLeaves(
        List.create(
            diffs.flatMap((diff) => {
                switch (diff.op) {
                    case '+': {
                        return diff.values.map((leaf) =>
                            leaf.addMarks(ISet.create([Mark.create(MARK_DIFF_ADDED)]))
                        );
                    }
                    case '-': {
                        return diff.values.map((leaf) =>
                            leaf.addMarks(ISet.create([Mark.create(MARK_DIFF_DELETED)]))
                        );
                    }
                    case '=': {
                        return diff.values;
                    }
                    default:
                        assertNever(diff);
                }
            })
        )
    );
}

/**
 * Split a text into leaves of words.
 */
function splitTextInWords(text: Text): Leaf[] {
    const result: Leaf[] = [];

    text.leaves.forEach((leaf) => {
        const words = splitWords(leaf.text);
        words.forEach((word) => {
            result.push(leaf.merge({ text: word }));
        });
    });

    return result;
}
